import { Provider as ReduxProvider, useDispatch, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/store";
import Router from "@/router";
import React, { Fragment, PropsWithChildren, useEffect, useMemo } from "react";
import { ConfigProvider, MappingAlgorithm, theme, ThemeConfig } from "antd";
import zhCN from "antd/locale/zh_CN";
import "dayjs/locale/zh-cn";
import { getTheme } from "@/store/selectors";
import { AliasToken } from "antd/es/theme/interface";
import { getSystemSet } from "@/request/modules/gen_api_4059";
import { setSystemConfig } from "@/store/actions";
import { CrudConfigProvider } from "@magicbe/antd-crud";

const { darkAlgorithm } = theme;

const App: React.FC = () => {
    return (
        <ReduxProvider store={store}>
            <PersistGate persistor={persistor}>
                <AntdProvider>
                    <SystemConfig>
                        <Router />
                    </SystemConfig>
                </AntdProvider>
            </PersistGate>
        </ReduxProvider>
    );
}

export default App;

const AntdProvider: React.FC<PropsWithChildren> = ({ children }) => {

    const theme = useSelector(getTheme);

    const token = useMemo<Partial<AliasToken>>(() => {
        if (theme === "dark") {
            return ({
                borderRadius: 3,
                colorPrimary: "#89DCeb"
            });
        }

        return ({ borderRadius: 3, colorPrimary: "#1E66F5" });
    }, [theme]);

    const algorithm = useMemo<MappingAlgorithm[]>(() => {
        if (theme === "light") return [/**compactAlgorithm */];
        return [darkAlgorithm, /**compactAlgorithm */];
    }, [theme]);

    const theme_config: ThemeConfig = {
        token,
        algorithm,
    }

    return (
        <ConfigProvider locale={zhCN} theme={theme_config}>
            <CrudConfigProvider locale={zhCN} theme={theme_config}>
                {children}
            </CrudConfigProvider>
        </ConfigProvider>
    )
}

const SystemConfig: React.FC<PropsWithChildren> = ({ children }) => {

    const dispatch = useDispatch();

    const loadAppSources = () => {
        getSystemSet().then(({ data }) => {
            dispatch(setSystemConfig(data));
            if (!data.name) return;
            document.title = data.name;
        });
    }

    useEffect(loadAppSources, []);

    return (
        <Fragment>
            {children}
        </Fragment>
    )
}